import { Button, Group, Modal, NumberInput, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUpdateInventorySettings } from 'src/Hooks/Mutations/useInventoryMutations';

import styles from './InventoryDepreciationModal.module.scss';
import { Props } from './InventoryDepreciationModal.types';

/** The house wear rule: one place, for managers who know their contract. */
const InventoryDepreciationModal = ({ opened, onClose, rate, max }: Props) => {
  const { t } = useTranslation();
  const [values, setValues] = useState({ rate, max });
  const { mutate, isLoading } = useUpdateInventorySettings();
  useEffect(() => setValues({ rate, max }), [rate, max, opened]);

  const save = () =>
    mutate(
      { depreciationRatePercent: values.rate, depreciationMaxPercent: values.max },
      {
        onSuccess: onClose,
        onError: () => {
          notifications.show({
            color: 'red',
            message: t('inventory.settings.error', "La règle n'a pas pu être changée."),
          });
        },
      }
    );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('inventory.settings.title', 'Règle d’usure de la maison')}
    >
      <div className={styles.container}>
        <Text size="sm">
          {t(
            'inventory.settings.text',
            'Elle sert à calculer la valeur actuelle de chaque objet. Si votre contrat d’assurance en indique une, reprenez-la.'
          )}
        </Text>
        <NumberInput
          label={t('inventory.settings.rate', 'Usure par an')}
          suffix=" %"
          min={0}
          max={100}
          allowDecimal={false}
          value={values.rate}
          onChange={value => setValues(current => ({ ...current, rate: Number(value) || 0 }))}
        />
        <NumberInput
          label={t('inventory.settings.max', 'Usure au plus')}
          suffix=" %"
          min={0}
          max={100}
          allowDecimal={false}
          value={values.max}
          onChange={value => setValues(current => ({ ...current, max: Number(value) || 0 }))}
        />
        <Group justify="flex-end">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button loading={isLoading} onClick={save}>
            {t('common.save', 'Enregistrer')}
          </Button>
        </Group>
      </div>
    </Modal>
  );
};

export default InventoryDepreciationModal;
